import { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Check, Layers, Search } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface TierComboboxProps {
  selected: number[];
  onChange: (tiers: number[]) => void;
}

const TIERS = [0, ...Array.from({ length: 10 }, (_, i) => i + 1)];

const TierCombobox = ({ selected, onChange }: TierComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (isMobile) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isMobile, open]);

  const toggle = (tier: number) => {
    onChange(selected.includes(tier) ? selected.filter((t) => t !== tier) : [...selected, tier]);
  };

  const removeTier = (tier: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter((t) => t !== tier));
  };

  const handleOpen = () => {
    if (open) {
      setOpen(false);
      setSearch("");
      return;
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const tierLabel = (t: number) => t === 0 ? "Sem tier" : `${t}`;
  const tierLabelFull = (t: number) => t === 0 ? "Sem tier" : `Tier ${t}`;

  const filtered = TIERS.filter(t =>
    !search.trim() || tierLabelFull(t).toLowerCase().includes(search.toLowerCase()) || `${t}`.includes(search.trim())
  );

  const listContent = (
    <>
      <div className="px-3 py-2 border-b border-border/40">
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar tier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent font-pixel text-[8px] sm:text-[7px] text-foreground outline-none placeholder:text-muted-foreground/60"
          />
          {search && (
            <button onClick={() => setSearch("")} className="cursor-pointer">
              <X className="w-3 h-3 text-muted-foreground/60" />
            </button>
          )}
        </div>
      </div>

      {selected.length > 0 && (
        <div className="px-3 py-1.5 border-b border-border/40 flex flex-wrap gap-1">
          {selected.sort((a, b) => a - b).map((t) => (
            <span key={t} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1.5 py-0.5 rounded-sm font-pixel text-[7px] whitespace-nowrap">
              {tierLabel(t)}
              <X className="w-2.5 h-2.5 cursor-pointer hover:text-destructive" onClick={(e) => removeTier(t, e)} />
            </span>
          ))}
        </div>
      )}

      <div className={isMobile ? "flex-1 overflow-auto" : "max-h-[200px] overflow-auto"}>
        {filtered.length === 0 ? (
          <div className="px-3 py-4 font-pixel text-[8px] sm:text-[7px] text-muted-foreground text-center">Nenhum tier</div>
        ) : (
          filtered.map((tier) => {
            const active = selected.includes(tier);
            return (
              <button
                key={tier}
                onClick={() => toggle(tier)}
                className="w-full flex items-center gap-2 px-3 py-2 sm:py-1.5 font-pixel text-[8px] sm:text-[7px] hover:bg-primary/10 active:bg-primary/20 transition-colors cursor-pointer text-left"
              >
                <div className={`w-4 h-4 sm:w-3 sm:h-3 border rounded-sm flex items-center justify-center ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                  {active && <Check className="w-2.5 h-2.5 sm:w-2 sm:h-2 text-primary-foreground" />}
                </div>
                <span className={`flex-1 ${active ? "text-primary" : "text-muted-foreground"}`}>{tierLabel(tier)}</span>
              </button>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <div ref={ref} className="relative flex flex-col gap-0.5">
        <label className="font-pixel text-[6px] text-muted-foreground flex items-center gap-1">
          <Layers className="w-2.5 h-2.5" /> Tier
        </label>
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 px-2 py-1.5 tibia-border bg-card rounded-sm font-pixel text-[7px] text-muted-foreground cursor-pointer min-w-[90px] max-w-[160px]"
        >
          <div className="flex-1 flex items-center gap-1 overflow-hidden">
            {selected.length === 0 ? (
              <span className="text-muted-foreground/60">Todos</span>
            ) : (
              <div className="flex items-center gap-0.5 overflow-hidden">
                {selected.sort((a, b) => a - b).slice(0, 3).map((t) => (
                  <span key={t} className="flex items-center gap-0.5 bg-primary/20 text-primary px-1 rounded-sm text-[6px] whitespace-nowrap">
                    {t}
                    <X className="w-2 h-2 cursor-pointer hover:text-destructive" onClick={(e) => removeTier(t, e)} />
                  </span>
                ))}
                {selected.length > 3 && (
                  <span className="text-[6px] text-primary">+{selected.length - 3}</span>
                )}
              </div>
            )}
          </div>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && !isMobile && (
          <div className="absolute top-full left-0 mt-1 z-50 tibia-border bg-card rounded-sm py-1 min-w-[100px] max-h-[200px] overflow-auto shadow-lg">
            {TIERS.map((tier) => {
              const active = selected.includes(tier);
              return (
                <button
                  key={tier}
                  onClick={() => toggle(tier)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 font-pixel text-[7px] hover:bg-primary/10 transition-colors cursor-pointer text-left"
                >
                  <div className={`w-3 h-3 border rounded-sm flex items-center justify-center ${active ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                    {active && <Check className="w-2 h-2 text-primary-foreground" />}
                  </div>
                  <span className={`flex-1 ${active ? "text-primary" : "text-muted-foreground"}`}>{tierLabel(tier)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {open && isMobile && (
        <div className="fixed inset-0 z-[200] bg-background flex flex-col">
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/40">
            <span className="font-pixel text-[9px] text-foreground">Selecionar Tier</span>
            <button onClick={() => { setOpen(false); setSearch(""); }} className="font-pixel text-[8px] text-primary cursor-pointer px-2 py-1">
              Fechar
            </button>
          </div>
          {listContent}
        </div>
      )}
    </>
  );
};

export default TierCombobox;
